import { tss } from 'tss-react/mui';

import {
  ListItemText,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useState } from 'react';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import type { ChangeOwnerDialogProps } from '../../actions/move/FileManagerChangeOwnerConfirmDialog';
import { FileManagerChangeOwnerConfirmDialog } from '../../actions/move/FileManagerChangeOwnerConfirmDialog';

export const FILE_OWNER_SELECTOR_PARENT_FRAGMENT = graphql(`
  fragment fileOwnerSelectorParentFields on File {
    _id
    owner {
      ... on Group {
        _id
        name
      }

      ... on Service {
        _id
        name
      }
    }
  }
`);

const FILE_OWNER_SELECTOR_FILE_FRAGMENT = graphql(`
  fragment fileOwnerSelectorFileFields on File {
    folder
    ...fileManagerChangeOwnerFileFields
  }
`);

export const FILE_OWNER_SELECTOR_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment fileOwnerSelectorFilesForOwnerFields on FilesForOwner {
    owner {
      ... on Group {
        _id
        name
      }
      ... on Service {
        _id
        name
      }
    }
  }
`);

export interface OwnerCandidate {
  __typename: 'Group' | 'Service';
  _id: string | null;
  name: string | null;
  isRoot?: boolean;
}

interface Props {
  file: DocumentType<typeof FILE_OWNER_SELECTOR_FILE_FRAGMENT>;
  filesForOwner: DocumentType<
    typeof FILE_OWNER_SELECTOR_FILES_FOR_OWNER_FRAGMENT
  >;
  parent: DocumentType<typeof FILE_OWNER_SELECTOR_PARENT_FRAGMENT> | null;
  rootOwnerId: string;
  ownerCandidates: OwnerCandidate[];
  canEdit: boolean;
  canLockRoot: boolean;
  wholeGroupName: string;
}

const useStyles = tss.create(({ theme }) => {
  const ownerFontSize = theme.typography.caption.fontSize;

  return {
    static: {
      fontSize: ownerFontSize,
      maxWidth: 150,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },

    smallSelectorSelect: {
      fontSize: ownerFontSize,
      maxWidth: 150,
    },

    smallSelectorMenuItem: {
      fontSize: ownerFontSize,
      paddingTop: 10,
      paddingBottom: 10,
    },

    selectorText: {
      fontSize: ownerFontSize,
    },

    smallSelectorItemPrimary: {
      fontSize: ownerFontSize,
    },

    smallSelectorItemSecondary: {
      fontSize: ownerFontSize,
    },

    infoItem: {
      opacity: 0.8,
    },
  };
});

export const FileOwnerSelector = ({
  file,
  filesForOwner,
  parent,
  rootOwnerId,
  ownerCandidates,
  canEdit,
  canLockRoot,
  wholeGroupName,
}: Props) => {
  const [changeOwnerDialogOpen, setChangeOwnerDialogOpen] =
    useState<ChangeOwnerDialogProps>();
  const { classes } = useStyles();

  const getStaticName = () => {
    if (parent?.owner && filesForOwner.owner._id !== parent.owner._id) {
      return parent?.owner?.name;
    }

    if (file.folder && !canEdit) {
      const folderOwner = ownerCandidates.find(
        (candidate) => candidate._id === (file.owner && file.owner._id),
      );
      return folderOwner ? folderOwner.name : 'Unknown';
    }

    if (!file.folder) {
      return parent?.owner && filesForOwner.owner._id !== parent.owner._id
        ? parent.owner.name
        : wholeGroupName;
    }
  };

  const staticName = getStaticName();
  const getHelpText = () => {
    if (file.folder && !canEdit && canLockRoot) {
      return `You need to be an admin of ${staticName} in order to change this`;
    }

    return null;
  };

  const helpText = getHelpText();

  if (staticName) {
    const staticText = <div className={classes.static}>{staticName}</div>;
    return helpText ? (
      <Tooltip title={helpText}>{staticText}</Tooltip>
    ) : (
      staticText
    );
  }

  return (
    <>
      <TextField
        select
        size="small"
        value={(file.owner && file.owner._id) || ''}
        onChange={(event) => {
          const ownerId = event.target.value;
          const parentId = parent?._id;

          if (ownerId === file?.owner?._id) {
            return;
          }

          const ownerName =
            ownerCandidates.find((candidate) => candidate._id === ownerId)
              ?.name ?? '';

          setChangeOwnerDialogOpen({
            file,
            parentId,
            ownerId,
            rootOwnerId,
            isChangingParent: false,
            isFolder: Boolean(file.folder),
            isMovingToRoot: ownerId === rootOwnerId,
            ownerName,
            open: true,
            onClose: () => setChangeOwnerDialogOpen(undefined),
          });
        }}
        slotProps={{
          select: {
            classes: {
              select: classes.smallSelectorSelect,
            },
          },
        }}
      >
        {ownerCandidates.map((ownerCandidate) => (
          <MenuItem
            key={ownerCandidate._id || undefined}
            value={ownerCandidate._id || undefined}
            classes={{ root: classes.smallSelectorMenuItem }}
          >
            <Typography className={classes.selectorText}>
              {ownerCandidate.name}
            </Typography>
          </MenuItem>
        ))}
        <MenuItem disabled className={classes.infoItem}>
          <ListItemText
            primary={<em>Don&apos;t see what you were expecting?</em>}
            secondary="Only committees you are a member of will show up here."
            classes={{
              primary: classes.smallSelectorItemPrimary,
              secondary: classes.smallSelectorItemSecondary,
            }}
          />
        </MenuItem>
      </TextField>
      {changeOwnerDialogOpen?.open ? (
        <FileManagerChangeOwnerConfirmDialog {...changeOwnerDialogOpen} />
      ) : null}
    </>
  );
};
